import { toConsumableArray as _toConsumableArray, objectSpread2 as _objectSpread2, defineProperty as _defineProperty } from './_virtual/_rollupPluginBabelHelpers.js';
import { findToast, getToastPosition } from './toast.utils.esm.js';

var initialState = {
  top: [],
  'top-left': [],
  'top-right': [],
  'bottom-left': [],
  bottom: [],
  'bottom-right': [],
  'center-center': []
};
var globalToastStore = createStore(initialState);

/**
 * Store to track and manage toasts across all positions
 */
function createStore(initialState) {
  /**
   * Static id counter to create unique ids
   * for each toast
   */
  var state = initialState;
  var listeners = new Set();
  var setState = function setState(setStateFn) {
    state = setStateFn(state);
    listeners.forEach(function (l) {
      return l();
    });
  };
  return {
    getState: function getState() {
      return state;
    },
    subscribe: function subscribe(listener) {
      listeners.add(listener);
      return function () {
        // Delete all toasts on unmount
        setState(function () {
          return initialState;
        });
        listeners["delete"](listener);
      };
    },
    /**
     * Function to actually create a toast and add it
     * to the state at the specified position.
     * Will use global toast store unless notify is being run
     * by a local toast- which would need to pass it's own store
     */
    notify: function notify(message, options, localToastStore) {
      var toastStoreToUse = localToastStore !== null && localToastStore !== void 0 ? localToastStore : globalToastStore;
      var toast = createToast(message, options, toastStoreToUse);
      var position = toast.position,
        id = toast.id;
      setState(function (prevToasts) {
        var _prevToasts$position, _prevToasts$position2;
        var isTop = position.includes('top');

        /**
         * - If the toast is positioned at the top edges, the
         * recent toast stacks on top of the other toasts.
         *
         * - If the toast is positioned at the bottom edges, the recent
         * toast stacks below the other toasts.
         */
        var toasts = isTop ? [toast].concat(_toConsumableArray((_prevToasts$position = prevToasts[position]) !== null && _prevToasts$position !== void 0 ? _prevToasts$position : [])) : [].concat(_toConsumableArray((_prevToasts$position2 = prevToasts[position]) !== null && _prevToasts$position2 !== void 0 ? _prevToasts$position2 : []), [toast]);
        return _objectSpread2(_objectSpread2({}, prevToasts), {}, _defineProperty({}, position, toasts));
      });
      return id;
    },
    /**
     * Update a specific toast with new options based on the
     * passed `id`
     */
    update: function update(id, options) {
      if (!id) return;
      setState(function (prevState) {
        var nextState = _objectSpread2({}, prevState);
        var _findToast = findToast(nextState, id),
          position = _findToast.position,
          index = _findToast.index;
        if (position && index !== -1) {
          nextState[position][index] = _objectSpread2(_objectSpread2({}, nextState[position][index]), options);
        }
        return nextState;
      });
    },
    /**
     * Close all toasts at once.
     * If given positions, will only close those.
     */

    closeAll: function closeAll() {
      var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
        positions = _ref.positions;
      // only one setState here for perf reasons
      setState(function (prev) {
        var allPositions = ['bottom', 'bottom-right', 'bottom-left', 'top', 'top-left', 'top-right', 'center-center'];
        var positionsToClose = positions !== null && positions !== void 0 ? positions : allPositions;
        return positionsToClose.reduce(function (acc, position) {
          acc[position] = prev[position].map(function (toast) {
            return _objectSpread2(_objectSpread2({}, toast), {}, {
              requestClose: true
            });
          });
          return acc;
        }, _objectSpread2({}, prev));
      });
    },
    /**
     * Requests to close a toast based on its id and position
     */
    close: function close(id) {
      setState(function (prevState) {
        var position = getToastPosition(prevState, id);
        if (!position) return prevState;
        return _objectSpread2(_objectSpread2({}, prevState), {}, _defineProperty({}, position, prevState[position].map(function (toast) {
          // id may be string or number
          // eslint-disable-next-line eqeqeq
          if (toast.id == id) {
            return _objectSpread2(_objectSpread2({}, toast), {}, {
              requestClose: true
            });
          }
          return toast;
        })));
      });
    },
    /**
     * Delete a toast record at its position
     */
    removeToast: function removeToast(id, position) {
      setState(function (prevState) {
        return _objectSpread2(_objectSpread2({}, prevState), {}, _defineProperty({}, position, prevState[position].filter(function (toast) {
          return toast.id != id;
        })));
      });
    },
    isVisible: function isVisible(id) {
      var _findToast2 = findToast(state, id),
        position = _findToast2.position;
      return Boolean(position);
    }
  };
}

/**
 * Static id counter to create unique ids
 * for each toast
 */
var counter = 0;

/**
 * Create properties for a new toast
 */
function createToast(message, options, localToastStore) {
  var _options$id, _options$position;
  counter += 1;
  var id = (_options$id = options.id) !== null && _options$id !== void 0 ? _options$id : counter;
  var position = (_options$position = options.position) !== null && _options$position !== void 0 ? _options$position : 'top';
  // use global toastStore if local toastStore is not provided
  var toastStoreToUse = localToastStore !== null && localToastStore !== void 0 ? localToastStore : globalToastStore;
  return {
    id: "".concat(id),
    message: message,
    position: position,
    duration: options.duration,
    onCloseComplete: options.onCloseComplete,
    onRequestRemove: function onRequestRemove() {
      return toastStoreToUse.removeToast(String(id), position);
    },
    status: options.status,
    requestClose: false,
    description: options.description
  };
}

export { createStore, globalToastStore, initialState };
//# sourceMappingURL=toast-manager.esm.js.map
